import React, { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { groupSkills } from './skillGrouping';
import { SearchableSelect } from '@/components/SearchableSelect';

// Two-step skill multi-select: pick skill group -> pick skill -> Add.
// Keeps the dropdown short and shows chosen skills as removable chips grouped by group.
export function GroupedSkillMultiSelect({ skills = [], selectedIds = [], onChange, testidPrefix = 'skill' }) {
  const [grp, setGrp] = useState('');
  const [sk, setSk] = useState('');
  const groups = groupSkills(skills);
  const skillsById = Object.fromEntries(skills.map(s => [s.id, s]));
  const addable = skills.filter(s => (grp ? s.skill_group_id === grp : false) && !selectedIds.includes(s.id));

  const add = () => {
    if (!sk) { toast.error('Select a skill to add'); return; }
    onChange([...new Set([...selectedIds, sk])]); setSk('');
  };
  const remove = (id) => onChange(selectedIds.filter(x => x !== id));
  const selectedGroups = groupSkills(selectedIds.map(id => skillsById[id]).filter(Boolean));

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end gap-2 rounded-md border border-dashed border-slate-300 p-2">
        <div>
          <Label className="text-[10px] text-slate-400">Skill Group</Label>
          <Select value={grp || undefined} onValueChange={v => { setGrp(v); setSk(''); }}>
            <SelectTrigger className="h-8 w-48 text-xs" data-testid={`${testidPrefix}-group`}><SelectValue placeholder="Select group" /></SelectTrigger>
            <SelectContent>{groups.map(g => <SelectItem key={g.group_id} value={g.group_id}>{g.group_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] text-slate-400">Skill</Label>
          <div className="w-56">
            <SearchableSelect
              options={addable.map(s => ({ value: s.id, label: s.name, sub: s.skill_type }))}
              value={sk} onSelect={setSk} disabled={!grp}
              placeholder={grp ? 'Search skill…' : 'Pick a group first'}
              testId={`${testidPrefix}-select`}
            />
          </div>
        </div>
        <Button type="button" size="sm" variant="outline" className="h-8 gap-1" onClick={add} data-testid={`${testidPrefix}-add`}><Plus className="w-3.5 h-3.5" /> Add</Button>
      </div>
      {selectedIds.length === 0 ? <p className="text-[11px] text-slate-400">No skills selected yet.</p> : (
        <div className="space-y-2">
          {selectedGroups.map(g => (
            <div key={g.group_id}>
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">{g.group_name}</p>
              <div className="flex flex-wrap gap-1.5">
                {g.skills.map(s => (
                  <span key={s.id} className="text-[11px] px-2 py-1 rounded border bg-pink-50 text-pink-700 border-pink-300 inline-flex items-center gap-1" data-testid={`${testidPrefix}-chip-${s.id}`}>
                    {s.name}
                    <button type="button" onClick={() => remove(s.id)} className="hover:text-pink-900" data-testid={`${testidPrefix}-remove-${s.id}`}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
